/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import {green, red, yellow} from '@material-ui/core/colors';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import {
  Add,
  Cancel,
  CardMembership,
  CheckCircle,
  Edit,
  GetApp,
  Info,
  Save,
  Warning
} from '@material-ui/icons';
import moment from 'moment';
import {PropTypes} from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {getFunctions, updateFunction} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import {
  NOTARY_EDIT_DETAILS,
  NOTARY_FUNCTION_STATUS_ACTIVE,
  NOTARY_FUNCTION_STATUS_INACTIVE
} from '../utils/constants';
import ConfirmationDialog from './ConfirmationDialog';
import NotaryFunctionDialog from './NotaryFunctionDialog';
import NotarySuccessor from './NotarySuccessor';

const SIGNOFF = 'sign-off';
const ACTIVATE = 'activate';
const useStyles = makeStyles((theme) => ({
  title: {paddingTop: 0, paddingBottom: '1%', fontWeight: 550},
  list: {backgroundColor: theme.palette.background.default},
  downloadBtn: {marginTop: 12},
  listIcon: {
    color: 'rgba(0, 0, 0, 0.54)',
    padding: 12,
    marginRight: 10,
    overflow: 'visible'
  },
  submitButton: {float: 'right'},
  button: {marginBottom: 16},
  root: {flexGrow: 1},
  gridContainer: {margin: theme.spacing(1)},
  errorMsg: {marginTop: theme.spacing(5)},
  container: {display: 'flex', flexWrap: 'wrap'},
  paper: {padding: theme.spacing(3, 2), width: 'inherit', marginTop: '0.1em'},
  nested: {
    paddingLeft: theme.spacing(4)
  }
}));

/**
 * Formate the function dates to string.
 *
 * @param {Date} validFrom - The function valid from date.
 * @param {Date} validTo - The function valid to date.
 * @return {string} - The formatted function date as string.
 */
const formateDateToString = (validFrom, validTo) => {
  if (validTo) {
    return locale.formatString(
      locale.functionDateText,
      moment(validFrom).format(locale.localizedDateFormat),
      moment(validTo).format(locale.localizedDateFormat)
    );
  }
  return locale.formatString(
    locale.functionDateTextWithoutEnd,
    moment(validFrom).format(locale.localizedDateFormat)
  );
};

/**
 * Notary Registration view for submitting signed document.
 *
 * @param {P} props - The property data.
 * @return {*} - The rendered string.
 */
function NotaryFunctions(props) {
  const classes = useStyles();
  const [functions, setFunctions] = React.useState(props.functions);
  const addFunctionNotifications = (notifications) =>
    props.dispatch(addNotifications(notifications));
  const [openDialog, setOpenDialog] = React.useState(false);
  const currentFunction = functions[functions.length - 1];
  const [currentFunctionStatus, setCurrentFunctionStatus] = React.useState(
    currentFunction.status
  );
  const [enableSaveButton, setEnableSaveButton] = React.useState(false);
  const [showFunctionSubmitDialog, setShowFunctionSubmitDialog] =
    React.useState(false);
  const [signOffReason] = React.useState(currentFunction.signOffReason);
  const [showSignOffDialog, setShowSignOffDialog] = React.useState(false);
  const [signOffComment] = React.useState(currentFunction.signOffComment);
  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 400,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9'
    }
  }))(Tooltip);

  const handleOpenDialog = (mode) => {
    setShowSignOffDialog(mode === SIGNOFF);
    setOpenDialog(true);
  };

  /**
   * Closes the edit function dialog.
   */
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  /**
   * Handles the function status change submit from the dialog.
   *
   * @param {string} executionDate - The execution date of the function status change.
   * @param {string} reason - The reason for the function deactivation.
   * @param {string} comment - The comment for the function deactivation.
   */
  const handleFunctionEditSubmit = (executionDate, reason, comment) => {
    const notaryFunction = {};
    if (showSignOffDialog) {
      // if the current function is active we terminate this function with reason and comment
      const notaryFunction = functions[functions.length - 1];
      notaryFunction.validTo = executionDate;
      notaryFunction.signOffReason = reason;
      notaryFunction.signOffComment = comment;
      notaryFunction.edited = true;
      // convert the current date to YYYY-MM-DD format for comparision
      if (executionDate < moment().format(locale.dateFormat)) {
        notaryFunction.status = NOTARY_FUNCTION_STATUS_INACTIVE;
      } else {
        notaryFunction.status = NOTARY_FUNCTION_STATUS_ACTIVE;
      }
      setCurrentFunctionStatus(notaryFunction.status);
      functions[functions.length - 1] = notaryFunction;
    } else {
      // if the current function is inactive we create a new function with validFrom date
      notaryFunction.validFrom = executionDate;
      notaryFunction.edited = true;
      notaryFunction.status = NOTARY_FUNCTION_STATUS_ACTIVE;
      functions.push(notaryFunction);
      setCurrentFunctionStatus(NOTARY_FUNCTION_STATUS_ACTIVE);
    }
    setEnableSaveButton(true);
    handleCloseDialog();
  };

  const saveFunction = () => {
    updateFunction(functions[functions.length - 1], props.personId).then(
      (response) => {
        if (response.updated) {
          refreshFunctions();
          setShowFunctionSubmitDialog(false);
          addFunctionNotifications([
            new Notification(
              locale.functionsSubmitCompleteNotification,
              'success',
              6000
            )
          ]);
        } else {
          addFunctionNotifications([
            new Notification(
              locale.functionsSubmitErrorNotification,
              'error',
              6000
            )
          ]);
        }
      }
    );
  };

  const refreshFunctions = () => {
    getFunctions(props.personId).then((functionResponse) => {
      setFunctions(functionResponse);
      const currentFunction = functionResponse[functionResponse.length - 1];
      setCurrentFunctionStatus(currentFunction.status);
      setEnableSaveButton(false);
    });
  };

  const getFunctionDetails = () => {
    const details = [];
    functions.forEach((func, index) => {
      if (func.edited) {
        details[index] = locale.formatString(
          locale.functionDetails,
          moment(func.validFrom).format(locale.localizedDateFormat),
          func.validTo
            ? moment(func.validTo).format(locale.localizedDateFormat)
            : '-',
          locale.getString('signOffReason' + func.signOffReason)
        );
      }
    });
    return details;
  };

  return (
    <div>
      <Paper className={classes.paper}>
        <Typography variant='subtitle1' gutterBottom className={classes.title}>
          {locale.functions}
        </Typography>
        {(currentFunctionStatus === NOTARY_FUNCTION_STATUS_INACTIVE ||
          currentFunction.validTo >= moment().format(locale.dateFormat)) &&
          !currentFunction.edited &&
          props.viewMode === NOTARY_EDIT_DETAILS && (
            <Tooltip title={locale.addFunctionButton} placement='top'>
              <Button
                variant='contained'
                color='primary'
                size='small'
                className={classes.button}
                onClick={() => handleOpenDialog(ACTIVATE)}
                startIcon={<Add />}
              >
                {locale.addButton}
              </Button>
            </Tooltip>
          )}
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={8}>
            <div className={classes.list}>
              <List component='div' dense={true}>
                {functions.map((functionEntity, index) => {
                  const lastIndex = index === functions.length - 1;
                  const viewEditMode = props.viewMode === NOTARY_EDIT_DETAILS;
                  return (
                    <div key={index}>
                      <ListItem className={classes.nested}>
                        <ListItemIcon>
                          {functionEntity.status ===
                          NOTARY_FUNCTION_STATUS_ACTIVE ? (
                            <CheckCircle
                              style={
                                functionEntity.validFrom >
                                moment().format(locale.dateFormat)
                                  ? {color: yellow[500]}
                                  : {color: green[500]}
                              }
                            />
                          ) : (
                            <Cancel style={{color: red[500]}} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={locale.notary}
                          secondary={formateDateToString(
                            functionEntity.validFrom,
                            functionEntity.validTo
                          )}
                        />

                        <ListItemSecondaryAction>
                          {functionEntity.edited && (
                            <HtmlTooltip
                              className={classes.listIcon}
                              title={
                                <React.Fragment>
                                  <Typography color='inherit'>
                                    {locale.functionNotSubmittedTooltipTitle}
                                  </Typography>
                                  <div>
                                    {
                                      locale.functionNotSubmittedTooltipDescription
                                    }
                                  </div>
                                </React.Fragment>
                              }
                            >
                              <Warning />
                            </HtmlTooltip>
                          )}
                          {viewEditMode &&
                            lastIndex &&
                            functionEntity.status ===
                              NOTARY_FUNCTION_STATUS_ACTIVE &&
                            !functionEntity.validTo &&
                            !functionEntity.edited && (
                              <React.Fragment>
                                <IconButton
                                  edge='end'
                                  aria-label='function-edit'
                                  onClick={() => handleOpenDialog(SIGNOFF)}
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  edge='end'
                                  aria-label='function-edit'
                                  className={classes.downloadBtn}
                                  onClick={() =>
                                    window.open(
                                      '/notaries/' +
                                        props.personId +
                                        '/templates/' +
                                        functionEntity.id +
                                        '/INSCRIPTION_DECREE',
                                      '_blank'
                                    )
                                  }
                                >
                                  <GetApp />
                                </IconButton>
                              </React.Fragment>
                            )}
                          {functionEntity.status !==
                            NOTARY_FUNCTION_STATUS_ACTIVE &&
                            viewEditMode && (
                              <React.Fragment>
                                <HtmlTooltip
                                  className={classes.listIcon}
                                  title={
                                    <React.Fragment>
                                      <Typography color='inherit'>
                                        {locale.signOffInformations}
                                      </Typography>
                                      <strong>{locale.signOffReason}</strong>
                                      <div>
                                        {locale.getString(
                                          'signOffReason' +
                                            (functionEntity.signOffReason
                                              ? functionEntity.signOffReason
                                              : signOffReason)
                                        )}
                                      </div>
                                      <strong>{locale.signOffComment}</strong>
                                      <div>
                                        {functionEntity.signOffComment
                                          ? functionEntity.signOffComment
                                          : signOffComment}
                                      </div>
                                    </React.Fragment>
                                  }
                                >
                                  <Info />
                                </HtmlTooltip>
                                {!functionEntity.edited && (
                                  <IconButton
                                    edge='end'
                                    aria-label='function-edit'
                                    className={classes.downloadBtn}
                                    onClick={() =>
                                      window.open(
                                        '/notaries/' +
                                          props.personId +
                                          '/templates/' +
                                          functionEntity.id +
                                          '/CESSATION_DECREE',
                                        '_blank'
                                      )
                                    }
                                  >
                                    <GetApp />
                                  </IconButton>
                                )}
                              </React.Fragment>
                            )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    </div>
                  );
                })}
              </List>
            </div>
            <br />
            {props.certificate && (
              <div>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  className={classes.title}
                >
                  {locale.certificate}
                </Typography>
                <div className={classes.list}>
                  <List component='div' dense={true}>
                    <ListItem className={classes.nested}>
                      <ListItemIcon>
                        <CardMembership />
                      </ListItemIcon>
                      <ListItemText
                        primary={locale.getString(
                          props.certificate.certificateType + 'Cert'
                        )}
                        secondary={formateDateToString(
                          props.certificate.usedFrom,
                          props.certificate.usedUntil
                        )}
                      />
                      <ListItemSecondaryAction>
                        <HtmlTooltip
                          title={
                            <React.Fragment>
                              <Typography color='inherit'>
                                {locale.certificateInformations}
                              </Typography>
                              <strong>{locale.certificateSubject}</strong>
                              <div>{props.certificate.subject}</div>
                              <strong>{locale.certificateIssuer}</strong>
                              <div>{props.certificate.issuer}</div>
                              <strong>{locale.certificatePublicKey}</strong>
                              <div>
                                {props.certificate.publicKey.substring(0, 55) +
                                  '...'}
                              </div>
                            </React.Fragment>
                          }
                        >
                          <Info />
                        </HtmlTooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </div>
              </div>
            )}
          </Grid>
          <Grid item xs={4}>
            {props.viewMode === NOTARY_EDIT_DETAILS && (
              <div className={classes.submitButton}>
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  disabled={!enableSaveButton}
                  onClick={() => setShowFunctionSubmitDialog(true)}
                  startIcon={<Save />}
                >
                  {locale.saveButton}
                </Button>
                <IconButton
                  color='secondary'
                  variant='contained'
                  disabled={!enableSaveButton}
                  component='span'
                  aria-label='function-reset'
                  onClick={() => refreshFunctions()}
                >
                  <Cancel />
                </IconButton>
              </div>
            )}
          </Grid>
        </Grid>

        <NotaryFunctionDialog
          showSignOff={showSignOffDialog}
          open={openDialog}
          handleClose={handleCloseDialog}
          handleSubmit={handleFunctionEditSubmit}
          minDate={
            showSignOffDialog
              ? currentFunction.validFrom
              : currentFunction.validTo
          }
        />
        <ConfirmationDialog
          open={showFunctionSubmitDialog}
          dialogText={locale.submitFunctionsDetails}
          title={locale.submitFunctionsDialogTitle}
          handleClose={() => setShowFunctionSubmitDialog(false)}
          handleSubmit={() => saveFunction()}
          detailList={getFunctionDetails()}
        />
      </Paper>
      {(props.viewMode === NOTARY_EDIT_DETAILS ||
        NOTARY_FUNCTION_STATUS_INACTIVE === currentFunctionStatus) && (
        <NotarySuccessor
          editMode={props.viewMode === NOTARY_EDIT_DETAILS}
          notaryId={props.personId}
        />
      )}
    </div>
  );
}

NotaryFunctions.propTypes = {
  dispatch: PropTypes.func.isRequired,
  functions: PropTypes.array.isRequired,
  personId: PropTypes.number.isRequired,
  certificate: PropTypes.object,
  viewMode: PropTypes.string.isRequired
};

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

export default connect(null, mapDispatchToProps)(NotaryFunctions);
